import { CATALOG_CURRENCIES } from '@/shared/catalogs/currencies'
import type { ClientV2Dossier } from '../services/clients-admin-v2.service'

/**
 * El expediente v2 traducido a lo que el formulario interno necesita RECORDAR
 * mientras se edita.
 *
 * Reemplaza a `IClientFetch`, que era el documento crudo de v1 y arrastraba
 * media máquina de estados muerta —`state`, `currentState`, `history`,
 * `workflow`— que ninguna pantalla lee desde que el cliente dejó de tener
 * estados propios. Acá viven los ocho campos que el formulario efectivamente
 * consulta: quién es, cómo se llama, quién lo tocó y si está bloqueado.
 *
 * `id` es el `cli_`. El formulario direccionaba por el `_id` de Mongo, y ese es
 * exactamente el hábito que esta tajada viene a romper.
 */
export type ClientDossierOriginal = {
  id: string
  code: number
  typePerson: 'individual' | 'juridical'
  name: string
  advisor: { id: string; fullname: string } | null
  /**
   * Empresa y agencia con su NOMBRE, no sólo la referencia. La ficha las
   * mostraba resolviendo el id contra un lookup; el expediente ya sabe cómo se
   * llaman, y hacer que la pantalla lo averigüe de nuevo es pedirle a la vista
   * que repita una consulta cuya respuesta ya tiene en la mano.
   */
  company: { id: string; name: string } | null
  agency: { id: string; name: string } | null
  createdBy: { id: string; fullname: string } | null
  updatedBy: { id: string; fullname: string } | null
  createdAt: string | null
  updatedAt: string | null
  /** Con un flujo del portal abierto el formulario va a sólo-lectura. */
  hasActiveFlow: boolean
  activeFlowStatus: string | null
}

/**
 * La ficha EDITABLE, en el vocabulario del FORMULARIO.
 *
 * Las fechas se llaman `startAt`/`updateAt`/`endAt` y no como en el wire v2
 * (`startDate`…) porque el consumidor de este tipo es el formulario, que las
 * nombra así en sus campos, en su validación y en su store. Traducirlas acá
 * obligaría a que el form supiera de dónde vino el expediente; traducirlas al
 * GUARDAR pasa una sola vez, en el único lugar que ya habla con la API.
 *
 * Las referencias, en cambio, SÍ viajan como id público (`usr_`, `cmp_`,
 * `agc_`): no son vocabulario, son el valor, y es el que los selectores de la
 * ficha ofrecen y el que el contrato de escritura espera de vuelta.
 */
export type ClientDossierCard = {
  typePerson: 'individual' | 'juridical'
  advisor: string | null
  company: string | null
  agency: string | null
  contract: { startAt: string | null; updateAt: string | null; endAt: string | null }
  place: { country: string; department: number | null; municipality: number | null }
  riskLevel: string | null
  signed: boolean
}

/** `GTQ` → `QUETZAL`: el código del catálogo a la etiqueta que el asesor elige. */
const CURRENCY_LABELS = new Map(
  CATALOG_CURRENCIES.map((currency) => [String(currency.code), currency.other.default])
)

function isRow(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Una sección del expediente, con las KEYS que el formulario necesita.
 *
 * Cada fila repetible —cuentas, vehículos, ingresos, representantes— lleva en el
 * navegador un `id` uuid que es una key de React, no una entidad. El contrato v2
 * no la publica A PROPÓSITO: la sección se reemplaza como unidad y el servidor
 * nunca correlaciona filas. Se regenera acá al montar, que es donde nace igual
 * cuando el asesor agrega una.
 *
 * El recorrido es GENÉRICO —toda fila de una sección recibe key— en vez de una
 * lista de rutas: una sección nueva no obliga a acordarse de agregarla acá, y
 * olvidarse era exactamente el bug que se quiere evitar.
 *
 * Cuando el guardar hable v2, el camino de vuelta tiene que SACAR estas keys: no
 * son del expediente, son de la pantalla.
 */
export function toFormSection<T>(section: T): T {
  return translate(section, false) as T
}

/**
 * El estado patrimonial, además, traduce la moneda.
 *
 * DOS CONVENCIONES CONVIVEN en el mismo expediente y NO se pueden tratar igual:
 * el patrimonial guarda la moneda como ETIQUETA (`QUETZAL`) y la muestra tal
 * cual, mientras la sección económica guarda el CÓDIGO (`GTQ`) y lo resuelve
 * contra el catálogo. v2 normaliza las dos a ISO, así que traducir de más deja
 * los montos económicos sin moneda —y sin monto, porque la fila no resuelve—.
 * Eso fue exactamente lo que pasó, y no lo vio ningún typecheck: lo vio la
 * pantalla, comparada contra la misma pantalla servida por v1.
 *
 * Cuando v2 no pudo traducir la moneda original manda `currencyLabel` con el
 * texto tal cual: ese gana.
 */
export function toFormEquitySection<T>(section: T): T {
  return translate(section, true) as T
}

function translate(value: unknown, withCurrencyLabels: boolean): unknown {
  if (Array.isArray(value)) {
    return value.map((item) =>
      isRow(item)
        ? { id: crypto.randomUUID(), ...(translate(item, withCurrencyLabels) as object) }
        : translate(item, withCurrencyLabels)
    )
  }
  if (!isRow(value)) return value

  const row: Record<string, unknown> = {}
  for (const [key, item] of Object.entries(value)) {
    if (withCurrencyLabels && key === 'currencyLabel') continue
    row[key] = translate(item, withCurrencyLabels)
  }
  /*
    `currency` y `currencyLabel` son EXCLUYENTES, y de ahí venía el hueco: acá se
    exigía `currency` presente para recién entonces mirar la etiqueta, que es
    justo el caso que nunca ocurre. Cuando v2 no puede traducir la moneda a ISO
    manda `currencyLabel` y OMITE `currency` —ver `stripRowKeys`—, así que la
    condición nunca se cumplía y la celda salía vacía. Producción tenía una
    cuenta HSBC en dírhams mostrando la moneda en blanco.
  */
  const label = value.currencyLabel
  const hasLabel = typeof label === 'string' && label.trim() !== ''
  if (withCurrencyLabels && (typeof value.currency === 'string' || hasLabel)) {
    row.currency = hasLabel
      ? label
      : (CURRENCY_LABELS.get(value.currency as string) ?? value.currency)
  }
  return row
}

/**
 * Nombre legible del expediente. El individual se nombra por su `fullname` ya
 * armado y la jurídica por su razón social: son dos campos distintos porque son
 * dos cosas distintas, y colapsarlos en uno obliga a cada pantalla a saber cuál
 * mirar según el tipo de persona.
 */
export function dossierName(dossier: ClientV2Dossier): string {
  if (dossier.typePerson === 'juridical') {
    return String(dossier.juridical?.applicant?.companyName ?? '')
  }
  const applicant = dossier.individual?.applicant as { fullname?: string } | undefined
  return String(applicant?.fullname ?? '')
}

export function toDossierOriginal(dossier: ClientV2Dossier): ClientDossierOriginal {
  return {
    id: dossier.id,
    code: dossier.code,
    typePerson: dossier.typePerson,
    name: dossierName(dossier),
    advisor: dossier.advisor
      ? { id: dossier.advisor.id, fullname: dossier.advisor.fullname }
      : null,
    company: dossier.company ? { id: dossier.company.id, name: dossier.company.name } : null,
    agency: dossier.agency ? { id: dossier.agency.id, name: dossier.agency.name } : null,
    createdBy: dossier.createdBy
      ? { id: dossier.createdBy.id, fullname: dossier.createdBy.fullname }
      : null,
    updatedBy: dossier.updatedBy
      ? { id: dossier.updatedBy.id, fullname: dossier.updatedBy.fullname }
      : null,
    createdAt: dossier.createdAt ?? null,
    updatedAt: dossier.updatedAt ?? null,
    hasActiveFlow: dossier.hasActiveFlow,
    activeFlowStatus: dossier.activeFlowStatus ?? null
  }
}

/**
 * La ficha editable. Las referencias viajan como ID PÚBLICO —`usr_`, `cmp_`,
 * `agc_`— porque es lo que el contrato de escritura espera de vuelta: el
 * formulario deja de conocer ObjectIds en las dos direcciones.
 */
export function toDossierCard(dossier: ClientV2Dossier): ClientDossierCard {
  return {
    typePerson: dossier.typePerson,
    advisor: dossier.advisor?.id ?? null,
    company: dossier.company?.id ?? null,
    agency: dossier.agency?.id ?? null,
    contract: {
      startAt: dossier.contract?.startDate ?? null,
      updateAt: dossier.contract?.updateDate ?? null,
      endAt: dossier.contract?.endDate ?? null
    },
    place: {
      country: dossier.place?.country ?? 'GT',
      department: dossier.place?.department ?? null,
      municipality: dossier.place?.municipality ?? null
    },
    riskLevel: dossier.riskLevel ?? null,
    signed: dossier.signed
  }
}
